import { environment } from '../../environments/environment';

const TENANT_PARAM = 'tenant';
const APPNAME_KEY = 'appName';
const USER_DETAILS_KEY = 'loggedInUserDetails';
const AUTH_TOKEN_KEY = 'authToken';

/**
 * CENTRAL tenant/session store.
 *
 * All tenant- and session-scoped identity (appName, tenant, loggedInUserDetails,
 * authToken) is kept in **sessionStorage**, NOT localStorage. sessionStorage is scoped
 * to a single browser tab, which is what isolates tenants: a user who has access to more
 * than one tenant can open each in its own tab without one tab's tenant state bleeding
 * into another. localStorage is shared across every tab of the origin, which previously
 * let a second-tenant tab read the first tenant's appName and route its API calls to the
 * wrong backend (cross-tenant data exposure).
 *
 * Every reader/writer of these keys must go through this module (or read the same
 * sessionStorage key) — never localStorage — so the two stores can never disagree.
 */

/** Extracts the tenant segment from the URL path (the segment after 'obmui'), lowercased. */
function getTenantFromPath(): string | null {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const obmuiIndex = pathSegments.findIndex(s => s.toLowerCase() === 'obmui');
  let seg: string | null = null;
  if (obmuiIndex !== -1 && pathSegments.length > obmuiIndex + 1) {
    seg = pathSegments[obmuiIndex + 1];
  } else if (pathSegments.length > 0 && pathSegments[0].toLowerCase() !== 'obmui') {
    seg = pathSegments[0];
  }
  return seg ? seg.toLowerCase() : null;
}

/**
 * Gets the backend URL for the current tenant/customer.
 *
 * Resolution order:
 *   1. Valid tenant in the URL path — the MAG->SM entry point. This is AUTHORITATIVE:
 *      it overwrites the tab's stored appName, so a launch of a different tenant (even
 *      reusing the same tab) can never keep routing to the previous tenant's backend.
 *   2. Stored appName (per-tab sessionStorage) — for internal SPA navigations, which
 *      carry no tenant segment because <base href> is the static "/obmui/".
 *   3. Previously stored tenant.
 *   4. Default tenant (QA/PROD have none -> null).
 *
 * Note: Angular UI is COMMON for all customers; the backend is CUSTOMER-SPECIFIC,
 *       determined by AppName -> environment.tenants[appName] -> backend URL.
 */
export function getTenant(): string | null {
  // Priority 1: valid tenant in the URL path (authoritative entry point).
  const tenantFromPath = getTenantFromPath();
  if (tenantFromPath && environment.tenants[tenantFromPath]) {
    // Overwrite this tab's stored identity so it always matches the launched tenant.
    sessionStorage.setItem(APPNAME_KEY, tenantFromPath);
    sessionStorage.setItem(TENANT_PARAM, tenantFromPath);
    return environment.tenants[tenantFromPath];
  }

  // Priority 2: AppName stored for this tab (post-login).
  const appName = sessionStorage.getItem(APPNAME_KEY);
  if (appName) {
    const appNameLower = appName.toLowerCase();
    if (environment.tenants[appNameLower]) {
      return environment.tenants[appNameLower];
    }
  }

  // Priority 3: previously stored tenant.
  const tenantFromStorage = sessionStorage.getItem(TENANT_PARAM);
  if (tenantFromStorage && tenantFromStorage in environment.tenants) {
    return environment.tenants[tenantFromStorage];
  }

  // Priority 4: default tenant.
  if (environment.tenants['default']) {
    return environment.tenants['default'];
  }

  return null;
}

/**
 * Sets the AppName after receiving it from backend (post-MAG authentication).
 * This determines which customer-specific backend to use for all API calls.
 */
export function setAppName(appName: string): void {
  if (appName) {
    sessionStorage.setItem(APPNAME_KEY, appName.toLowerCase());
    console.log('[TenantUtil] AppName set:', appName);
  }
}

/**
 * Gets the current AppName (customer identifier from MAG claims).
 */
export function getAppName(): string | null {
  return sessionStorage.getItem(APPNAME_KEY);
}

/**
 * Gets the current tenant name (e.g. 'sem1') — the stored tenant, falling back to the
 * stored appName. Used to build backend login/redirect URLs. Returns '' when unknown.
 */
export function getTenantName(): string {
  return sessionStorage.getItem(TENANT_PARAM) ?? sessionStorage.getItem(APPNAME_KEY) ?? '';
}

/**
 * Maps an old Legacy FQDN (that now DNS-resolves to this SPA after cutover) to its
 * tenant appName, or null if the current host isn't a known legacy host.
 * Configured per-env via environment.legacyHostTenant (host -> tenant).
 */
export function getTenantFromLegacyHost(): string | null {
  const map = (environment as any).legacyHostTenant as Record<string, string> | undefined;
  if (!map) return null;
  return map[window.location.hostname.toLowerCase()] ?? null;
}

/**
 * Clears tenant/appName data (used during logout).
 */
export function clearTenantData(): void {
  sessionStorage.removeItem(TENANT_PARAM);
  sessionStorage.removeItem(APPNAME_KEY);
}

/* ---- Logged-in user details (per-tab) ---- */

/** Raw JSON string of the logged-in user details, or null. */
export function getUserDetailsRaw(): string | null {
  return sessionStorage.getItem(USER_DETAILS_KEY);
}

/** Stores the logged-in user details (accepts an object or a JSON string). */
export function setUserDetails(details: unknown): void {
  const value = typeof details === 'string' ? details : JSON.stringify(details);
  sessionStorage.setItem(USER_DETAILS_KEY, value);
}

/** Removes the logged-in user details. */
export function clearUserDetails(): void {
  sessionStorage.removeItem(USER_DETAILS_KEY);
}

/* ---- Auth token marker (per-tab) ---- */

export function getAuthToken(): string | null {
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
}

// Session storage key for redirect state
const REDIRECTING_KEY = 'sm_redirecting';

/**
 * Sets the redirecting flag to indicate app is navigating away.
 * Used to show a loading spinner instead of error pages during redirect.
 */
export function setRedirecting(value: boolean): void {
  if (value) {
    sessionStorage.setItem(REDIRECTING_KEY, 'true');
  } else {
    sessionStorage.removeItem(REDIRECTING_KEY);
  }
}

/**
 * Checks if the app is currently in a redirect state.
 */
export function isRedirecting(): boolean {
  return sessionStorage.getItem(REDIRECTING_KEY) === 'true';
}
